const stripTrailingSlash = (value) => (value ? String(value).replace(/\/+$/, '') : '');
const isLocalhostUrl = (value) => /^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0)(?::\d+)?(?:\/.*)?$/i.test(value);
const isReservedPythonPort = (value) => /^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0):5002(?:\/.*)?$/i.test(value);

const DEV_FRONTEND_PORTS = new Set(['3000', '3001', '3002']);
const DEFAULT_LOCAL_BASE = process.env.REACT_APP_DEV_API_URL || 'http://localhost:5001';
const DEFAULT_PROD_BASE =
  process.env.REACT_APP_PROD_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  '';

const PROD_FALLBACK_BASE = null; // Disabled - only use primary backend

const getWindow = () => (typeof window === 'undefined' ? undefined : window);

const readRuntimeOverride = () => {
  const w = getWindow();
  if (!w) return null;
  return w.__API_BASE_URL__ || w.__API_URL__ || null;
};

const readEnvOverride = () => {
  const candidate =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    null;
  if (!candidate) return null;
  const trimmed = stripTrailingSlash(candidate);
  const w = getWindow();
  const isLocalCandidate = isLocalhostUrl(trimmed);
  if (isLocalCandidate && w && w.location) {
    const host = String(w.location.hostname || '').toLowerCase();
    if (host && !/^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(host)) {
      return null;
    }
  }
  if (isLocalCandidate && isReservedPythonPort(trimmed)) {
    return null;
  }
  return trimmed;
};

const readCachedOverride = () => {
  const w = getWindow();
  if (!w || !w.sessionStorage) return null;
  try {
    const cached = w.sessionStorage.getItem('apiBase');
    if (cached && isLocalhostUrl(cached) && isReservedPythonPort(cached)) {
      return null;
    }
    return cached;
  } catch (_) {
    return null;
  }
};

export const rememberApiBase = (value) => {
  const w = getWindow();
  if (!w || !w.sessionStorage || !value) return;
  try {
    w.sessionStorage.setItem('apiBase', stripTrailingSlash(value));
  } catch (_) {
    /* ignore sessionStorage issues */
  }
};

export const hasExplicitApiBase = () => !!(readRuntimeOverride() || readEnvOverride());

const detectImplicitBase = () => {
  const w = getWindow();
  if (!w || !w.location) return DEFAULT_LOCAL_BASE;
  const { protocol = 'http:', hostname = 'localhost', port = '' } = w.location;
  const normalizedHost = (hostname || 'localhost').toLowerCase();
  const normalizedPort = port || (protocol === 'https:' ? '443' : '80');
  const isLocalHost = /^(localhost|127\.0\.0\.1)$/i.test(normalizedHost);

  if (isLocalHost) {
    if (DEV_FRONTEND_PORTS.has(normalizedPort)) {
      return DEFAULT_LOCAL_BASE;
    }
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  // Auto-detect API backend from known production domains
  // When the frontend is served from a static site (e.g. halitrade.com),
  // route API calls to the corresponding backend (api.halitrade.com).
  const prodApiMap = {
    'halitrade.com': 'https://api.halitrade.com',
    'www.halitrade.com': 'https://api.halitrade.com',
    'halisoft.biz': 'https://api.halitrade.com',
    'www.halisoft.biz': 'https://api.halitrade.com',
  };
  const mappedApi = prodApiMap[normalizedHost];
  if (mappedApi) {
    return mappedApi;
  }

  // Non-local deployments use an explicit production base so that static hosting
  // (e.g. Render static site + separate API) can still reach the backend.
  if (DEFAULT_PROD_BASE) {
    return stripTrailingSlash(DEFAULT_PROD_BASE);
  }
  return PROD_FALLBACK_BASE ? stripTrailingSlash(PROD_FALLBACK_BASE) : '';
};

export const resolveApiBase = () => {
  const explicit = readRuntimeOverride() || readEnvOverride();
  if (explicit) return stripTrailingSlash(explicit);

  const cached = readCachedOverride();
  if (cached) return stripTrailingSlash(cached);

  return stripTrailingSlash(detectImplicitBase());
};

export const DEV_BACKEND_CANDIDATES = [
  'http://localhost:5001',
  'http://127.0.0.1:5001',
  // Port 5002 is reserved for Python Factor Tools API - do not probe
  // 'http://localhost:5002',
  // 'http://127.0.0.1:5002',
  'http://localhost:5003',
  'http://127.0.0.1:5003',
  'http://localhost:5004',
  'http://127.0.0.1:5004',
  'http://localhost:5005',
  'http://127.0.0.1:5005',
];

const prodCandidateSources = [DEFAULT_PROD_BASE, PROD_FALLBACK_BASE]
  .filter(Boolean)
  .map(stripTrailingSlash);

export const PROD_BACKEND_CANDIDATES = Array.from(new Set(prodCandidateSources)).filter(Boolean);
